import { getAppMode, getExtensionVersion } from './browser';

export const BASE_API_URL = '/sales-extension/api';

export type RequestInterceptor = (request: RequestInit) => RequestInit;
export type ResponseInterceptor = (response: Response) => Promise<Response>;
export type ResponseTypeWithStatus<T> = { data: T; status: number };
export type ResponseType<T, IncludeStatus extends boolean> = IncludeStatus extends true ? ResponseTypeWithStatus<T> : T;

const parseErrorBody = (errorBody: unknown): string => {
  if (!errorBody || typeof errorBody !== 'object') {
    return '';
  }

  const messages: string[] = [];
  for (const [field, errors] of Object.entries(errorBody as Record<string, unknown>)) {
    if (Array.isArray(errors)) {
      messages.push(`${field}: ${errors.join(', ')}`);
    } else if (typeof errors === 'string') {
      messages.push(`${field}: ${errors}`);
    }
  }
  return messages.join('; ');
};

export class HttpError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
  }
}

export class HttpClient {
  private static requestInterceptors: RequestInterceptor[] = [this.addHeaders];
  private static responseInterceptors: ResponseInterceptor[] = [];

  private static addHeaders(request: RequestInit) {
    let updatedHeaders = { ...request.headers };

    if (['POST', 'PUT'].includes(request.method || '')) {
      updatedHeaders = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...updatedHeaders,
      };
    }

    const appMode = getAppMode();
    if (appMode) {
      updatedHeaders = {
        ...updatedHeaders,
        'X-SW-SALES-EXTENSION-mode': appMode,
      };
    }

    const version = getExtensionVersion();
    if (version) {
      updatedHeaders = {
        ...updatedHeaders,
        'X-SW-SALES-EXTENSION-version': version,
      };
    }
    return { ...request, headers: updatedHeaders };
  }

  static registerRequestInterceptor(interceptor: RequestInterceptor) {
    this.requestInterceptors.push(interceptor);

    return () => {
      this.requestInterceptors = this.requestInterceptors.filter((i) => i !== interceptor);
    };
  }

  static registerResponseInterceptor(interceptor: ResponseInterceptor) {
    this.responseInterceptors.push(interceptor);

    return () => {
      this.responseInterceptors = this.responseInterceptors.filter((i) => i !== interceptor);
    };
  }

  static get<T>(url: string, options?: RequestInit): Promise<T> {
    return this.request<T>('GET', url, undefined, options) as Promise<T>;
  }

  static getWithStatus<T>(url: string, options?: RequestInit): Promise<ResponseTypeWithStatus<T>> {
    return this.request<T>('GET', url, undefined, options, true) as Promise<ResponseTypeWithStatus<T>>;
  }

  static post<T>(url: string, body: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>('POST', url, body, options) as Promise<T>;
  }

  static postForm<T>(url: string, body: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>('POST', url, body, options, false, false) as Promise<T>;
  }

  static postWithStatus<T>(url: string, body: unknown, options?: RequestInit): Promise<ResponseTypeWithStatus<T>> {
    return this.request<T>('POST', url, body, options, true) as Promise<ResponseTypeWithStatus<T>>;
  }

  static postWithRawResponse(
    url: string,
    body: unknown,
    options?: RequestInit
  ): Promise<ResponseTypeWithStatus<Response>> {
    return this.request<Response>('POST', url, body, options, true, true, true) as Promise<
      ResponseTypeWithStatus<Response>
    >;
  }

  static put<T>(url: string, body: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>('PUT', url, body, options) as Promise<T>;
  }

  static delete<T>(url: string, options?: RequestInit): Promise<T> {
    return this.request<T>('DELETE', url, undefined, options) as Promise<T>;
  }

  private static async request<T>(
    method: string,
    url: string,
    body?: unknown,
    options?: RequestInit,
    includeStatus?: boolean,
    stringifyBody = true,
    includeRawResponse: boolean = false
  ): Promise<ResponseType<T, boolean>> {
    const request: RequestInit = this.requestInterceptors.reduce((prevValue, interceptor) => interceptor(prevValue), {
      method,
      body: body ? (stringifyBody ? JSON.stringify(body) : body) : undefined,
      ...options,
    } as RequestInit);

    let response: Response | null = null;

    try {
      response = await fetch(url, request);

      response = await this.responseInterceptors.reduce((responsePromise, interceptor) => {
        return responsePromise.then(interceptor);
      }, Promise.resolve(response));

      if (!response.ok) {
        let errorMessage = `HTTP error ${response.status}: ${response.statusText}`;
        try {
          const errorBody: unknown = await response.json();
          const parsedError = parseErrorBody(errorBody);
          if (parsedError) {
            errorMessage = parsedError;
          }
        } catch {
          // If we can't parse the error body, use the generic message
        }
        throw new HttpError(errorMessage, response.status);
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return null as unknown as ResponseType<T, false>;
      } else {
        console.error('Request failed:', error);
        throw error;
      }
    }

    if (response?.status === 204) {
      return includeStatus
        ? ({ data: null as unknown as T, status: response.status } as ResponseType<T, true>)
        : (null as unknown as ResponseType<T, false>);
    }

    let data: T | null = null;
    try {
      if (includeRawResponse) {
        data = response as T;
      } else {
        const responseText = await response?.text();
        if (responseText) {
          data = responseText ? (JSON.parse(responseText) as T) : null;
        }
      }
    } catch (error) {
      console.error('Failed to parse JSON response', error);
    }

    if (includeStatus) {
      return { data: data as T, status: response?.status } as ResponseType<T, true>;
    } else {
      return data as ResponseType<T, false>;
    }
  }
}
